import React, {useState, useEffect, useCallback, useMemo} from 'react'
import {useAsync} from '../utils/use-async'
import {useLocation} from 'react-router-dom'
import {CountrySelection} from './country-selection'
import {useAuthClient} from '../utils/use-auth-client'
import {IndustryCategorySelection} from './industry-category-selection'
import {AccountInformation} from './account-information'
import {IndustrySelection} from './industry-selection'
import {toast} from 'react-toastify'
import {OnboardingProgram} from './onboarding-program'
import PaymentDetails from './payment-details'
import {useFlags} from 'launchdarkly-react-client-sdk'
import {useTrackLR} from '../utils/use-track-lr'
import {Catalog} from './catalog'
import useAutoCoupons from '../utils/use-auto-coupons'
import {useUserRoles} from '../utils/use-user-roles'
import {getAuthByRole} from '../utils/get-screen-auth-by-role'
import {
  CC_CURRENCIES,
  CC_PLANS,
  KEAP_FAMILY_CODES,
  KEAP_FAMILY_ANNUAL_CODES,
  MC_2_0_PLANS,
} from '../utils/constants'
import {Commitment} from './commitment'

import {
  MainContainer,
  WizardContainer,
  StepsContainer,
} from '../components/ui/new-order'
import {useUserFeatures} from '../context/user-features-context'
import {applyProductRulesBeforeLoading} from '../features/CatalogGrid/utils/productPreloadRules'
import PartnerSelection from './partner-selection'
import {selectUserData} from '../app-store/selector.js'
import {useSelector, useDispatch, useStore} from 'react-redux'
import {shallowEqual} from 'react-redux'
import {
  selectMergedCoupons,
  selectManualCoupons,
  selectDefaultCoupons,
  setMergedCoupons,
  setDefaultCoupons as setDefaultCouponsAction,
  resetAllCouponState,
} from '../app-store/coupon'

function Container({children, frontendSideNav}) {
  if (frontendSideNav) {
    return <div className="d-flex">{children}</div>
  }
  return children
}

const WORKFORCE_CODE = 'wc_plus-5-thryv-m2m'
const ONBOARDING_FEE_CODE = 'thryvprfee'
const KICKOFF_REQUIRED_CODES = [
  'bc_plus-5-thryv-m2m',
  'bc_pro-5-thryv-m2m',
  'bc_unlm-5-thryv-m2m',
  'bc-6-thryv-m2m',
  'bc_ent-6-thryv-m2m',
  'bc_ent-unlm-5-thryv-m2m',
  'mc_plus-5-thryv-m2m',
  'mc_pro-5-thryv-m2m',
  'seobase-5-thryv-m2m',
  'maxhvac-5-thryv-m2m',
  'maxroof-5-thryv-m2m',
  'maxelect-5-thryv-m2m',
  'maxplumb-5-thryv-m2m',
  'keap_max-5-thryv-m2m',
  'keap_ultimate-5-thryv-m2m',
  'keap_max-5-thryv-part-m2m',
  'keap_ultimate-5-thryv-part-m2m',
  'addon-5-leadslift-m2m',
  'addon-6-mc-socialcontentgen-m2m',
  'premier-5-thryv-annual',
  'mc_premier-5-thryv-m2m',
]

const isWorkforceProduct = product =>
  product?.code === WORKFORCE_CODE ||
  product?.name?.includes('Workforce Center')

function NewOrder() {
  const location = useLocation()
  const {roles} = useUserRoles()

  const {user} = useSelector(selectUserData)

  const initialStep = useCallback(
    () => (roles?.includes('PartnerOps') ? 0 : 1),
    [roles],
  )

  const {frontendSideNav} = useFlags()
  const {state} = location
  const [order, setOrder] = useState(state?.order || {})
  const [isNewAccountOrderCreated, setIsNewAccountOrderCreated] =
    useState(false)
  const [orderData5CU, setOrderData5CU] = useState({})
  const [order5NUId, setOrder5NUId] = useState(null)
  const [order5SelectedLines, setOrder5SelectedLines] = useState({})
  const [order5LinesDiscounts, setOrder5LinesDiscounts] = useState([])
  const [order5OnboardingApptId, setOrder5OnboardingApptId] = useState(null)
  const [order5CreativeApptId, setOrder5CreativeApptId] = useState(null)
  const [
    order5ThryvSuccessServicesApptId,
    setOrder5ThryvSuccessServicesApptId,
  ] = useState(null)
  const [currentStep, setCurrentStep] = useState(
    state?.currentStep || initialStep(),
  )
  const [accountSubscriptionData, setAccountSubscriptionData] = useState({})
  const [kickoffApptId, setKickoffApptId] = useState(null)
  const [creativeApptId, setCreativeApptId] = useState(null)
  const [thryvSuccessServicesApptId, setThryvSuccessServicesApptId] =
    useState(null)
  const [onboardingCallDate, setOnboardingCallDate] = useState(null)
  const [creativeCallDate, setCreativeCallDate] = useState(null)
  const [thryvSuccessServicesCallDate, setThryvSuccessServicesCallDate] =
    useState(null)
  const [processing, setProcessing] = useState(false)
  const [allowInvoice, setAllowInvoice] = useState(false)
  const [showSmsOption, setShwoSmsOption] = useState(false)
  const [accountInfo, setAccountInfo] = useState({})
  const [isWebsiteValid, setIsWebsiteValid] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState('Credit Card')
  const [allSelectedPurchaseIntent, setAllSelectedPurchaseIntent] = useState({
    firstPurchaseIntent: {},
    secondPurchaseIntent: {},
    thirdPurchaseIntent: {},
    csmExtraInfo: '',
  })
  const couponDispatch = useDispatch()
  const couponStore = useStore()
  const orderCouponCodes = useSelector(selectMergedCoupons, shallowEqual)
  const ccCouponInfo = useSelector(selectManualCoupons, shallowEqual)
  const defaultCouponInfo = useSelector(selectDefaultCoupons, shallowEqual)
  const setDefaultCouponInfo = useCallback(
    value => couponDispatch(setDefaultCouponsAction(value)),
    [couponDispatch],
  )
  const setOrderCouponCodes = useCallback(
    valueOrUpdater => {
      if (typeof valueOrUpdater === 'function') {
        const current = couponStore.getState().ccCouponState.mergedCoupons
        couponDispatch(setMergedCoupons(valueOrUpdater(current)))
      } else {
        couponDispatch(setMergedCoupons(valueOrUpdater))
      }
    },
    [couponDispatch, couponStore],
  )
  const [clientInitials, setClientInitials] = useState('')
  const [cartProducts, setCartProducts] = useState([])
  const [all5PlanPrices, setAll5PlanPrices] = useState(() =>
    roles?.includes('PartnerOps') ? null : state?.all5PlanPrices || null,
  )
  const [isEaidFound, setIsEaidFound] = useState(false)
  const [cohortEaid, setCohortEaid] = useState(false)
  const [isCohort, setIsCohort] = useState(false)
  const [keapAccount, setKeapAccount] = useState(
    accountInfo?.keapAccount ||
      accountSubscriptionData?.accountInfo?.keapAccount ||
      false,
  )
  const [keapID, setKeapID] = useState(accountInfo?.keapID || '')
  const [partnerType, setPartnerType] = useState({name: '', value: ''})
  const client = useAuthClient()
  const userFeatures = useUserFeatures()
  const {
    TL_MC_Access: isTLMCAccess,
    TL_Tax_Plan_Enabled: isNewTLPlanEnabled = false,
    Keap_Account_Ability,
    // Custom_Data_Service_Access: hasCustomDataServiceAccess,
    Custom_Data_For_You_Access: hasCustomDataFYAccess,
  } = userFeatures
  const isNewTLMCAccessible = isTLMCAccess
  const catalogPlanPrices = useMemo(() => {
    if (!all5PlanPrices) return null
    const rulesFiltered = applyProductRulesBeforeLoading(
      all5PlanPrices,
      userFeatures,
    )
    const existingTLWithoutTax =
      accountSubscriptionData?.childSubscriptions?.some(
        sub => sub.code === 'tl-mc-5-thryv-m2m',
      )
    const codeToRemove = isNewTLPlanEnabled
      ? existingTLWithoutTax
        ? 'tl-tax-5-thryv-m2m'
        : 'tl-mc-5-thryv-m2m'
      : 'tl-tax-5-thryv-m2m'
    const filterList = list =>
      list?.filter(item => item?.code !== codeToRemove) ?? []
    return {
      ...rulesFiltered,
      centers: filterList(rulesFiltered.centers),
      apps: filterList(rulesFiltered.apps),
      addons: filterList(rulesFiltered.addons),
    }
  }, [
    all5PlanPrices,
    isNewTLPlanEnabled,
    accountSubscriptionData,
    userFeatures,
  ])
  const [hasKeapAccountAbility, setHasKeapAccountAbility] =
    useState(Keap_Account_Ability)
  const [keapTrailAppId, setKeapTrailAppId] = useState(null)
  const [keapTextVoiceQty, setKeapTextVoiceQty] = useState(1)
  const [keapTextVoiceQtyAnnual, setKeapTextVoiceQtyAnnual] = useState(1)
  const [keapAddContactsQty, setKeapAddContactsQty] = useState(1)
  const [keapAddContactsQtyAnnual, setKeapAddContactsQtyAnnual] = useState(1)
  const [keapAddContactsQtyGraphlyM2m, setKeapAddContactsQtyGraphlyM2m] =
    useState('')
  const [plusThisPremiumQtyGraphlyM2m, setPlusThisPremiumQtyGraphlyM2m] =
    useState('')
  const [keapAddContactsQtyGraphlyAnnual, setKeapAddContactsQtyGraphlyAnnual] =
    useState('')
  const [plusThisPremiumQtyGraphlyAnnual, setPlusThisPremiumQtyGraphlyAnnual] =
    useState('')
  console.log('keapAddContactsQtyAnnual :: ', keapAddContactsQtyAnnual)
  console.log('user roles', roles)
  const isReseller = getAuthByRole(['reseller'], roles)

  const {run} = useAsync({
    status: 'pending',
  })
  const customEvent = order?.accountInformation && {
    key: 'new-order',
    companyEmail: order.accountInformation.email,
    companyName: order.accountInformation.company,
  }
  useTrackLR(customEvent)
  let clientType = null
  if (keapAccount) {
    clientType = 'keap'
  }

  const {resetAutoCoupons} = useAutoCoupons({
    selectedPackage: order.accountInformation?.selectedPackage,
    selectedIndustryId: order.industryCategoryId,
    selectedCountry: order.selectedCountry,
    selectedCentersAndApps: order.selectedCentersAndApps,
    selectedAddons: order.selectedAddons,
    currentStep,
    setOrderCouponCodes,
    isEaidFound,
    isCohort,
    clientType,
  })

  // Clean up coupon state when leaving the order screen
  useEffect(() => {
    return () => couponDispatch(resetAllCouponState())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const isoValue = order?.selectedCountry?.isoValue
    if (['AU', 'NZ'].includes(isoValue)) {
      setSelectedPaymentMethod('Invoice')
    } else {
      setSelectedPaymentMethod('Credit Card')
    }
  }, [order?.selectedCountry?.isoValue])

  const removeDuplicatesFromArray = (arrayObject, comparisionAttribute) => {
    const uniqueArrayElements = new Set()
    return arrayObject.filter(item => {
      const keyValue = item[comparisionAttribute]
      return uniqueArrayElements.has(keyValue)
        ? false
        : (uniqueArrayElements.add(keyValue), true)
    })
  }
  console.log('orderCouponCodes :: ', orderCouponCodes)

  const TOTAL_STEPS = 9

  const effectiveSelectedCentersAndApps = useMemo(() => {
    const selectedCentersAndApps = order?.selectedCentersAndApps

    if (!selectedCentersAndApps) {
      return selectedCentersAndApps
    }

    const centers = selectedCentersAndApps?.centers || []
    const apps = selectedCentersAndApps?.apps || []
    const oneTimes = selectedCentersAndApps?.oneTimes || []

    const hasWorkforce = [...centers, ...apps].some(isWorkforceProduct)
    const hasOtherKickoffProduct = [...centers, ...apps].some(product => {
      if (isWorkforceProduct(product)) {
        return false
      }

      return (
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          product?.code,
        ) || KICKOFF_REQUIRED_CODES.includes(product?.code)
      )
    })

    const isWorkforceOnlyKickoff = hasWorkforce && !hasOtherKickoffProduct
    const hasOnboardingFee = oneTimes.some(
      fee => fee?.code === ONBOARDING_FEE_CODE,
    )

    if (!kickoffApptId || !isWorkforceOnlyKickoff || hasOnboardingFee) {
      return selectedCentersAndApps
    }

    const onboardingFee = all5PlanPrices?.oneTimeFees?.find(
      fee => fee?.code === ONBOARDING_FEE_CODE,
    )

    if (!onboardingFee) {
      return selectedCentersAndApps
    }

    const currency = CC_CURRENCIES[order?.selectedCountry?.value]
    const amount =
      onboardingFee?.currencies?.find(item => item.currency === currency)
        ?.unitAmount ??
      onboardingFee?.amount ??
      onboardingFee?.unitAmount ??
      0

    return {
      ...selectedCentersAndApps,
      oneTimes: [
        ...oneTimes,
        {
          ...onboardingFee,
          quantity: onboardingFee?.quantity || 1,
          amount,
          unitAmount: amount,
        },
      ],
    }
  }, [
    all5PlanPrices,
    kickoffApptId,
    order?.selectedCentersAndApps,
    order?.selectedCountry?.value,
  ])

  const isSEOIncluded =
    effectiveSelectedCentersAndApps?.centers?.some(({name}) =>
      name.includes('SEO'),
    ) ||
    effectiveSelectedCentersAndApps?.apps?.some(({name}) =>
      name.includes('SEO'),
    )

  useEffect(() => {
    if (all5PlanPrices) return
    const get5PlanPrices = async () => {
      const url = 'plans/5/v1/list'
      const response = await client(url)
      setAll5PlanPrices(response)
    }
    get5PlanPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // When navigating from AccountSetup (multi-location flow), state.order carries
  // accountInformation and CCAccountInfo but orderData5CU starts empty — seed it
  // so captureOrder5NewUpgrade can build the correct order header.
  useEffect(() => {
    if (state?.order?.accountInformation && !Object.keys(orderData5CU).length) {
      const ai = state.order.accountInformation
      setOrderData5CU({
        order: state.order,
        accountInfo: {
          email: ai.email,
          firstName: ai.firstName,
          lastName: ai.lastName,
          company: ai.company,
          companyPhone: ai.companyPhone,
          mobile: ai.mobile,
          billTo: ai.billTo,
          // On-behalf-of Sales Rep: captureOrder5NewUpgrade builds order_header
          // from orderData5CU.accountInfo and reads salesRepCode → sales_rep_id,
          // behalfEmail → on_behalf_of, xpCode → xp_code. The account-setup
          // handoff carries these on accountInformation (behalf = the selected
          // rep's FederationIdentifier); forward them here so order_header is
          // populated too. Empty strings when no on-behalf rep was selected.
          salesRepCode: ai.behalf || '',
          behalfEmail: ai.behalfEmail || '',
          behalfFirst: ai.behalfFirst || '',
          behalfLast: ai.behalfLast || '',
          xpCode: ai.xpCode || '',
          billingAddress: {
            street1: ai.address,
            city: ai.city,
            region: ai.state,
            postalCode: ai.postCode,
            phone: ai.companyPhone,
          },
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // useEffect(() => {
  //   if (kickoffApptId && !roles.includes('PartnerOps')) {
  //     return () => {
  //       client(`plans/onboarding`, {
  //         data: {
  //           type: 'onboarding',
  //           country: order?.selectedCountry?.isoValue,
  //           demoApptId: kickoffApptId,
  //         },
  //         method: 'DELETE',
  //       })
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [kickoffApptId])

  // useEffect(() => {
  //   if (creativeApptId && !roles.includes('PartnerOps')) {
  //     return () => {
  //       client(`plans/creative`, {
  //         data: {
  //           type: 'creative',
  //           country: order?.selectedCountry?.isoValue,
  //           demoApptId: creativeApptId,
  //         },
  //         method: 'DELETE',
  //       })
  //     }
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [creativeApptId])

  // useEffect(() => {
  //   console.log('Array.isArray(ccCouponInfo) : ', Array.isArray(ccCouponInfo))
  //   // if (ccCouponInfo && orderCouponCodes.length === 0) {
  //   //   if (Array.isArray(ccCouponInfo)) setOrderCouponCodes(ccCouponInfo)
  //   //   else setOrderCouponCodes([ccCouponInfo])
  //   // }
  //   if (
  //     ccCouponInfo &&
  //     ccCouponInfo.length > 0 &&
  //     orderCouponCodes.length === 0
  //   ) {
  //     setOrderCouponCodes(removeDuplicatesFromArray([...ccCouponInfo], 'code'))
  //   }
  // }, [ccCouponInfo, orderCouponCodes])

  // useEffect(() => {
  //   if (
  //     defaultCouponInfo &&
  //     defaultCouponInfo.length > 0 &&
  //     orderCouponCodes &&
  //     Array.isArray(orderCouponCodes)
  //   ) {
  //     // Reset the autoCoupons
  //     resetAutoCoupons()
  //     //Based on the latest default coupon selection, remove incompatible coupons from orderCouponCodes
  //     let filteredOrderCouponCodes
  //     filteredOrderCouponCodes = [...orderCouponCodes]
  //     for (let coupon of defaultCouponInfo) {
  //       let incompatibleCoupons = coupon?.incompatibles?.split(',')
  //       filteredOrderCouponCodes = filteredOrderCouponCodes.filter(
  //         item =>
  //           !incompatibleCoupons.some(
  //             incompatibleCoupon => incompatibleCoupon === item.code,
  //           ),
  //       )
  //     }
  //     setOrderCouponCodes(
  //       removeDuplicatesFromArray(
  //         [...filteredOrderCouponCodes, ...defaultCouponInfo],
  //         'code',
  //       ),
  //     )
  //   }
  // }, [defaultCouponInfo])

  // useEffect(() => {
  //   if (autoCouponInfo && autoCouponInfo.length > 0)
  //     setOrderCouponCodes(
  //       removeDuplicatesFromArray(
  //         [...orderCouponCodes, ...autoCouponInfo],
  //         'code',
  //       ),
  //     )
  // }, [autoCouponInfo])

  useEffect(() => {
    if (
      ccCouponInfo &&
      ccCouponInfo.length > 0 &&
      orderCouponCodes.length > 0
    ) {
      // Reset the autoCoupons
      resetAutoCoupons()
      // Filter coupons that are incompatible with selected non-default coupon
      let filteredOrderCouponCodes
      let fileteredDefaultCouons
      // Fetch all the incompatible coupons of the selected manual coupons
      let incompatibleCoupons =
        ccCouponInfo.reduce(
          (incompatibles, coupon) =>
            coupon?.incompatibles && coupon?.incompatibles != 'null'
              ? coupon?.incompatibles + ',' + incompatibles
              : incompatibles,
          '',
        ).length > 0
          ? ccCouponInfo
              .reduce(
                (incompatibles, coupon) =>
                  coupon?.incompatibles && coupon?.incompatibles != 'null'
                    ? coupon?.incompatibles + ',' + incompatibles
                    : incompatibles,
                '',
              )
              .split(',')
          : null
      // Remove the incompatible coupon from the orderCouponCodes and defaultCouponInfo
      if (incompatibleCoupons && incompatibleCoupons.length > 0) {
        filteredOrderCouponCodes = orderCouponCodes.filter(
          item =>
            !incompatibleCoupons.some(
              incompatibleCoupon => incompatibleCoupon === item.code,
            ),
        )
        fileteredDefaultCouons = defaultCouponInfo.filter(
          item =>
            !incompatibleCoupons.some(
              incompatibleCoupon => incompatibleCoupon === item.code,
            ),
        )
      } else {
        filteredOrderCouponCodes = [...orderCouponCodes]
        fileteredDefaultCouons = [...defaultCouponInfo]
      }

      setOrderCouponCodes(
        removeDuplicatesFromArray(
          [...ccCouponInfo, ...filteredOrderCouponCodes],
          'code',
        ),
      )
      setDefaultCouponInfo(fileteredDefaultCouons)
    }
  }, [ccCouponInfo])

  const nextStep = data => {
    console.log('order obj - next step - new order.js', order)
    console.log('order data - next step - new order.js', data)

    setOrder({...order, ...data})
    let _currentStep = currentStep
    if (currentStep >= TOTAL_STEPS - 1) {
      _currentStep = TOTAL_STEPS
    } else {
      _currentStep = currentStep + 1
    }
    setCurrentStep(_currentStep)
  }
  const prevStep = () => {
    let _currentStep = currentStep
    if (currentStep <= initialStep()) {
      _currentStep = initialStep()
    } else {
      _currentStep = currentStep - 1
    }
    setCurrentStep(_currentStep)
  }

  const updateAddressInfo = (data, setShowModal) => {
    const insertData = {
      ...order,
      accountInformation: {
        ...order?.accountInformation,
        address: data?.street1,
        postCode: data?.postalCode,
        state: data?.region,
        companyPhone: data?.phone,
        ...data,
      },
    }

    delete insertData.accountInformation.postalCode
    delete insertData.accountInformation.street1
    delete insertData.accountInformation.region
    delete insertData.accountInformation.phone

    setOrder(insertData)
    setShowModal(false)
    setOrderData5CU({
      ...orderData5CU,
      accountInfo: {...orderData5CU.accountInfo, ...data},
    })
  }

  const updateInitials = initials => {
    setClientInitials(initials)
  }

  const findWebsiteEntitlement = centers =>
    centers
      .find(({entitlements}) =>
        entitlements?.some(
          ({name}) => name === 'Professionally Designed Website',
        ),
      )
      ?.entitlements?.find(
        ({name}) => name === 'Professionally Designed Website',
      )

  const findWebsiteApp = ({name}) => name === 'Professionally Designed Website'

  const formatCenters = (centers, callbackFn) =>
    centers
      .filter(callbackFn)
      .map(({code, quantity = 1, entitlements, startDate}) => {
        return {
          code,
          quantity,
          ...(startDate && new Date(startDate) >= new Date() && {startDate}),
          addons: entitlements?.map(({code, quantity}) => {
            // const entitlementQuantity =
            // code === 'ent-keap-included-contacts'
            //   ? 1500
            //   : code === 'ent-keap-included-user'
            //   ? 2
            //   : 1
            return {
              code,
              quantity: quantity ?? 1,
            }
          }),
        }
      })
  const formatApps = (apps, callbackFn) =>
    apps.filter(callbackFn).map(({code, quantity = 1}) => {
      return {
        code,
        quantity,
        addons: [],
      }
    })

  const handleSendLinkFailEmail = async contactMethod => {
    try {
      const data = {
        orderKeyer: user.network_id,
        contactMethod: contactMethod,
        onBehalf: accountInfo?.onBehalf || null,
      }
      const result = await run(client('orders/process/send-link-fail', {data}))
      console.log('log: sendlink fail email response --->', result)
    } catch (error) {
      toast.error('Error in sending failed Send Link email')
    }
  }

  const sendCCPaymentLink = async (contactMethod, contactInfo) => {
    const demoLeadId = order.accountInformation.leadID || order.leadId
    let {centers, apps, oneTimes} = effectiveSelectedCentersAndApps
    // const thryvMaxAddons = apps.find(({code}) => code === 'addon-5-keap-addons')
    const thryvLeadsId = (
      centers.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      ) ||
      apps.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      )
    )?.thryvLeadsId
    // const seoProposal = centers.find(({name}) => name.includes('SEO'))
    const seoProposal = centers.find(
      ({name}) => name.includes('SEO') && name !== 'AEO & SEO',
    )
    const alphaSeo =
      centers.find(({name}) => name === 'AEO & SEO') ||
      apps.find(({name}) => name === 'AEO & SEO')
    const sensisDomain = (
      findWebsiteEntitlement(centers) || apps.find(findWebsiteApp)
    )?.sensisSiteDomain
    const mcDomain = centers.find(
      ({name, code}) =>
        name.toLowerCase().includes('marketing') || MC_2_0_PLANS.includes(code),
    )?.mcDomain
    const tlDomain = (
      centers.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      ) ||
      apps.find(({code}) =>
        ['tl-mc-5-thryv-m2m', 'tl-tax-5-thryv-m2m', 'tl-5-thryv-m2m'].includes(
          code,
        ),
      )
    )?.tlDomain
    const keapDomain = centers.find(({code}) =>
      code.startsWith('keap_'),
    )?.keapDomain
    const parentSubscriptions = formatCenters(centers, ({code}) =>
      code.startsWith('cc_'),
    )
    const marketplace = apps.find(({code}) => code === 'appmarket-5-thryv-m2m')
    let childSubscriptions = []
    for (let center of centers) {
      if (center?.code.startsWith('cc_')) {
        continue
      } else {
        const formattedCenter = formatCenters(centers, ({name}) =>
          name.includes(center?.name),
        )
        childSubscriptions.push(formattedCenter[0])
      }
    }
    if (thryvLeadsId && isNewTLMCAccessible) {
      apps = apps.filter(
        ({name}) =>
          !['Starter Growth Package', 'Extended Growth Package'].includes(name),
      )
    }
    const isBCUnlimitedInOrder = childSubscriptions.some(
      ({code}) => code === 'bc_unlm-5-thryv-m2m',
    )
    const thryvMaxAddons = all5PlanPrices.apps.find(
      ({code}) => code === 'addon-5-keap-addons',
    )
    const thryvMaxAddonproducts = all5PlanPrices.apps
      .filter(
        app =>
          app.parentProduct === 'addon-5-keap-addons' &&
          app.code !== thryvMaxAddons?.code,
      )
      .map(item => item.code)

    const hasKeapFamilyCenterInOrder = centers.some(({code}) =>
      KEAP_FAMILY_CODES.includes(code),
    )
    const hasSelectedKeapChildAddon = apps.some(({code}) =>
      thryvMaxAddonproducts.includes(code),
    )

    const shouldAddKeapAddonsSubscription =
      Boolean(thryvMaxAddons) &&
      (apps.some(({code}) => code === 'addon-5-keap-addons') ||
        (hasKeapFamilyCenterInOrder && hasSelectedKeapChildAddon))

    const hasKeapAddonsSubscription = childSubscriptions.some(
      ({code}) => code === thryvMaxAddons?.code,
    )

    if (shouldAddKeapAddonsSubscription && !hasKeapAddonsSubscription) {
      childSubscriptions.push({
        code: thryvMaxAddons.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproducts.includes(item.code))
          .map(({code, quantity = 1}) => {
            if (code === 'addon-5-keap-textvoice-m2m') {
              quantity = keapTextVoiceQty
            }
            if (code === 'addon-5-keap-addcontacts-m2m') {
              quantity = keapAddContactsQty
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-m2m') {
              quantity = keapAddContactsQtyGraphlyM2m
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-m2m') {
              quantity = plusThisPremiumQtyGraphlyM2m
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }

    const thryvMaxAddonsAnnual = all5PlanPrices.apps.find(
      ({code}) => code === 'addon-5-keap-addons-annual',
    )
    const thryvMaxAddonproductsAnnual = all5PlanPrices.apps
      .filter(
        app =>
          app.parentProduct === 'addon-5-keap-addons-annual' &&
          app.code !== thryvMaxAddonsAnnual?.code,
      )
      .map(item => item.code)

    const hasKeapFamilyCenterAnnualInOrder = centers.some(({code}) =>
      KEAP_FAMILY_ANNUAL_CODES.includes(code),
    )
    const hasSelectedKeapChildAddonAnnual = apps.some(({code}) =>
      thryvMaxAddonproductsAnnual.includes(code),
    )

    const shouldAddKeapAddonsAnnualSubscription =
      Boolean(thryvMaxAddonsAnnual) &&
      (apps.some(({code}) => code === 'addon-5-keap-addons-annual') ||
        (hasKeapFamilyCenterAnnualInOrder && hasSelectedKeapChildAddonAnnual))

    const hasKeapAddonsAnnualSubscription = childSubscriptions.some(
      ({code}) => code === thryvMaxAddonsAnnual?.code,
    )

    if (
      shouldAddKeapAddonsAnnualSubscription &&
      !hasKeapAddonsAnnualSubscription
    ) {
      childSubscriptions.push({
        code: thryvMaxAddonsAnnual.code,
        quantity: 1,
        addons: apps
          .filter(item => thryvMaxAddonproductsAnnual.includes(item.code))
          .map(({code, quantity = 1}) => {
            if (code === 'addon-5-keap-addcontacts-annual') {
              quantity = keapAddContactsQtyAnnual
            }
            if (code === 'addon-5-keap-textvoice-annual') {
              quantity = keapTextVoiceQtyAnnual
            }
            if (code === 'addon-5-keap-legacy-graphly-contacts-annual') {
              quantity = keapAddContactsQtyGraphlyAnnual
            }
            if (code === 'addon-5-keap-legacy-plusthis-premium-trans-annual') {
              quantity = plusThisPremiumQtyGraphlyAnnual
            }
            return {
              code,
              quantity,
            }
          }),
      })
    }

    childSubscriptions.push({
      code: marketplace.code,
      quantity: 1,
      addons: apps
        .filter(({name, code}) => {
          if (thryvMaxAddonproducts.includes(code)) return false
          if (thryvMaxAddonproductsAnnual.includes(code)) return false
          if (code === 'addon-5-mktproeditor' && isBCUnlimitedInOrder) {
            return false
          }
          if (code === 'proservices-m2m') {
            return false
          }
          if (
            code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
            code === 'vert_clientsvcs-5-thryv-m2m' ||
            code === 'vert_mc_clientsvcs-5-thryv-ot' ||
            code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
            code === 'vert_clientsvcs-5-thryv-ot' ||
            code === 'vert_svcs-5-thryv-m2m' ||
            code === 'appmarket-5-thryv-m2m' ||
            code === 'addon-5-keap-addons' ||
            code === 'addon-5-keap-addons-annual' ||
            code === 'addon-5-listrefrsh-m2m' ||
            code === 'addon-5-stratcontent-m2m' ||
            code === 'addon-5-leadslift-bni-m2m' ||
            code === 'addon-5-visibilityboost' ||
            code === 'addon-ctn-bninat-m2m' ||
            code === 'addon-ctn-bninat-annual'
            // code === 'addon-5-keap-custdfy-ot'
          ) {
            return false
          }
          return (
            name !== 'AEO & SEO' &&
            name !== 'Thryv Leads' &&
            (name !== 'SEO Keywords') & (name !== 'AEO & SEO PowerBoost') /*&&
            name !== 'Keap Max' */ &&
            name !== 'Leads Lift' &&
            !code.startsWith('tl-')
          )
        })
        .map(({code, quantity = 1}) => {
          return {
            code,
            quantity,
          }
        }),
    })
    const tlApp = apps.find(({code}) => code.startsWith('tl-'))
    if (tlApp) {
      const tlStartDate =
        tlApp.startDate || tlApp.configuredOptions?.billingDate

      childSubscriptions.push({
        code: tlApp.code,
        quantity: tlApp.quantity || 1,
        addons: [],
        ...(tlStartDate &&
          new Date(tlStartDate) >= new Date() && {startDate: tlStartDate}),
      })
    }

    let isLeadLiftExist = apps.find(
      item => item.code === 'addon-5-leadslift-m2m',
    )
    let isLeadLiftBNIExist = apps.find(
      item => item.code === 'addon-5-leadslift-bni-m2m',
    )
    if (isLeadLiftExist) {
      childSubscriptions.push({
        code: isLeadLiftExist.code,
        quantity: isLeadLiftExist.quantity || 1,
        addons: [],
      })
    }

    if (isLeadLiftBNIExist) {
      childSubscriptions.push({
        code: isLeadLiftBNIExist.code,
        quantity: isLeadLiftBNIExist.quantity || 1,
        addons: [],
      })
    }

    const isVisibilityBoostExist = apps.find(
      item => item.code === 'addon-5-visibilityboost',
    )
    if (isVisibilityBoostExist) {
      childSubscriptions.push({
        code: isVisibilityBoostExist.code,
        quantity: isVisibilityBoostExist.quantity || 1,
        addons: (isVisibilityBoostExist.entitlements || []).map(
          ({code, quantity}) => ({
            code,
            quantity: quantity ?? 1,
          }),
        ),
      })
    }

    const ctnApp = apps.find(
      ({code}) =>
        code === 'addon-ctn-bninat-m2m' || code === 'addon-ctn-bninat-annual',
    )
    if (ctnApp) {
      childSubscriptions.push({
        code: ctnApp.code,
        quantity: ctnApp.quantity || 1,
        addons: [],
      })
    }
    const oneTimeFees = oneTimes.map(({code, quantity = 1}) => {
      return {
        code,
        quantity,
      }
    })

    // Hanlde new Alpha SEO
    const newAlphaSEO = apps.find(({name}) => name === 'AEO & SEO')

    if (newAlphaSEO) {
      const keyword = apps.find(({name}) => name === 'SEO Keywords')
      const powerboost = apps.find(({name}) => name === 'AEO & SEO PowerBoost')
      const seoStartDate =
        newAlphaSEO.startDate || newAlphaSEO.configuredOptions?.billingDate
      const seoSubscription = {
        code: newAlphaSEO.code,
        quantity: newAlphaSEO.amount || newAlphaSEO.quantity || 1,
        addons: [],
        ...(seoStartDate &&
          new Date(seoStartDate) >= new Date() && {startDate: seoStartDate}),
      }

      if (keyword) {
        seoSubscription.addons.push({
          code: keyword.code,
          quantity: keyword.quantity,
        })
      }

      if (powerboost) {
        seoSubscription.addons.push({
          code: powerboost.code,
          quantity: powerboost.quantity,
        })
      }

      childSubscriptions.push(seoSubscription)
    }

    for (let app of apps) {
      if (app?.name === 'Keap Max') {
        const formattedApp = formatApps(apps, ({name}) =>
          name.includes(app?.name),
        )
        childSubscriptions.push(formattedApp[0])
      } else if (
        app?.code === 'vert_mc_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_clientsvcs-5-thryv-m2m' ||
        app?.code === 'vert_mc_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_mc_web_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_clientsvcs-5-thryv-ot' ||
        app?.code === 'vert_svcs-5-thryv-m2m' ||
        app?.code === 'addon-5-listrefrsh-m2m' ||
        app?.code === 'addon-5-stratcontent-m2m'
        // app?.code === 'addon-5-keap-custdfy-ot'
      ) {
        const formattedApp = formatApps(apps, ({code}) => code === app?.code)
        childSubscriptions.push(formattedApp[0])
      }
    }

    const professionalServicesApp = apps.find(
      ({code}) => code === 'proservices-m2m',
    )
    const professionalServicesInSub = childSubscriptions.some(
      ({code}) => code === 'proservices-m2m',
    )

    if (professionalServicesApp && !professionalServicesInSub) {
      childSubscriptions.push({
        code: professionalServicesApp.code,
        quantity: 1,
        addons: [],
      })
    }

    const isDesignFeeWaived = isWebsiteValid
      ? oneTimeFees.filter(oneTimeFee => oneTimeFee.code !== 'thryvdesg2')
      : oneTimeFees

    const hasZapier = childSubscriptions?.find(item =>
      item.addons?.find(addOn => addOn.code === 'kp-zapier-750zaps-m2m'),
    )

    if (hasZapier) {
      childSubscriptions = childSubscriptions?.map(item => {
        if (
          item.addons?.find(addOn => addOn?.code === 'kp-zapier-750zaps-m2m')
        ) {
          item.addons = item.addons?.filter(
            addOn => addOn.code !== 'kp-zapier-750zaps-m2m',
          )
        }
        return item
      })
    }

    const data = {
      firstName: order.accountInformation.firstName,
      lastName: order.accountInformation.lastName,
      email: order.accountInformation.email,
      company: order.accountInformation.company,
      currency: CC_CURRENCIES[order.selectedCountry.value],
      industryId: order.template,
      businessAddress: {
        street: order.accountInformation.address,
        city: order.accountInformation.city,
        state: order.accountInformation.state,
        postalCode: order.accountInformation.postCode,
        country: order.selectedCountry.isoValue,
        phone: order.accountInformation.companyPhone,
      },
      contactPhone: contactInfo,
      thryvId: order.CCAccountInfo.thryvId,
      flow: 'new',
      sendLinkVia: contactMethod,
      parentSubscriptions,
      childSubscriptions,
      oneTimeFees: isDesignFeeWaived,
      isCohort,
      keapAccount,
      keapId: keapID || '',
    }
    if (cohortEaid) {
      data.cohortEaid = cohortEaid
    }
    if (
      order.selectedCountry.isoValue === 'KY' ||
      order.selectedCountry.isoValue === 'BB'
    ) {
      data.currency = 'USD'
    }
    if (kickoffApptId) {
      data.onboardingApptId = kickoffApptId
    }
    if (creativeApptId) {
      data.creativeApptId = creativeApptId
    }
    if (thryvSuccessServicesApptId) {
      data.thryvSuccessServicesApptId = thryvSuccessServicesApptId
    }
    if (order.accountInformation.xpCode?.length > 0) {
      data.xpCode = order.accountInformation.xpCode
    }
    if (order.accountInformation.salesRepOrPartner) {
      data.salesRepOrPartner = order.accountInformation.salesRepOrPartner
    }
    if (
      order.accountInformation.differentSalesRep?.value === true ||
      order.accountInformation.differentSalesRep?.value === 'true'
    ) {
      if (order.accountInformation.behalf) {
        data.behalfRepId = order.accountInformation.behalf
      }
      if (order.accountInformation.behalfFirst) {
        data.behalfFirstName = order.accountInformation.behalfFirst
      }
      if (order.accountInformation.behalfLast) {
        data.behalfLastName = order.accountInformation.behalfLast
      }
      if (order.accountInformation.behalfEmail) {
        data.behalfEmail = order.accountInformation.behalfEmail
      }
    }
    if (order.accountInformation.overrideRep) {
      if (order.accountInformation.overrideRepCode) {
        data.overrideRepId = order.accountInformation.overrideRepCode
      }
      if (order.accountInformation.overrideRepFirst) {
        data.overrideRepFirst = order.accountInformation.overrideRepFirst
      }
      if (order.accountInformation.overrideRepLast) {
        data.overrideRepLast = order.accountInformation.overrideRepLast
      }
      if (order.accountInformation.overrideRepEmail) {
        data.overrideRepEmail = order.accountInformation.overrideRepEmail
      }
    }
    if (
      ['AU', 'NZ'].includes(order.selectedCountry.isoValue) &&
      order.accountInformation.abn
    ) {
      data.abn = order.accountInformation.abn.replaceAll(' ', '')
    }
    if (data?.abn === '') {
      delete data.abn
    }
    if (order.onBoardingProgramData?.csmExtraInfo) {
      data.orderNotes = order.onBoardingProgramData.csmExtraInfo
    }
    if (thryvLeadsId) {
      data.thryvLeadsId = thryvLeadsId
    }
    if (alphaSeo) {
      data.seoProposalId = alphaSeo.alphaProposalId
      data.seoDomain = alphaSeo.alphaDomain
      // data.seoCategories = alphaSeo.alphaCategories
      // data.seoGeos = alphaSeo.alphaGeos
    } else if (newAlphaSEO) {
      data.seoProposalId = newAlphaSEO.alphaProposalId
      data.seoDomain = newAlphaSEO.alphaDomain
    }
    if (seoProposal) {
      data.seoProposalId = seoProposal.seoProposalId
      data.seoDomain = seoProposal.seoDomain
    }
    if (sensisDomain) {
      data.sensisDomain = sensisDomain
    }
    if (selectedPaymentMethod) {
      data.isInvoiceBilled = selectedPaymentMethod === 'Invoice'
    }
    if (mcDomain) {
      data.mcDomain = mcDomain
    }
    if (tlDomain) {
      data.tlDomain = tlDomain
    }
    if (keapDomain) {
      data.keapDomain = keapDomain
    }
    if (demoLeadId) {
      data.demoLeadId = demoLeadId
    }
    if (keapTrailAppId && keapTrailAppId.length > 0) {
      data.keapTrailId = keapTrailAppId
    }
    if (orderCouponCodes.find(x => x.code === 'transition75')) {
      const couponCodes = ['25month1', '25month2', '25month3']
      const {code} = order.selectedCentersAndApps.centers.find(({name}) =>
        CC_PLANS.some(plan => name.toLowerCase().includes(plan)),
      )
      const createUrl = couponCode =>
        `orders/coupon/validate?couponCode=${couponCode}&country=${order.selectedCountry.isoValue}&planCode=${code}`
      const [transitionCoupon1, transitionCoupon2, transitionCoupon3] =
        await Promise.all(
          couponCodes.map(async code => {
            const url = createUrl(code)
            return await client(url)
          }),
        )
      const transitionCouponIndex = orderCouponCodes.findIndex(
        addonInfo => addonInfo.code === 'transition75',
      )
      const couponsCopy = [...orderCouponCodes]
      // captureOrderDiscounts(couponsCopy)

      couponsCopy.splice(transitionCouponIndex, 1)
      couponsCopy.push(transitionCoupon1, transitionCoupon2, transitionCoupon3)

      const COUPON_CODES = couponsCopy
        .filter(coupon => {
          return coupon.isValid
        })
        .map(coupon => {
          return coupon.code
        })
      if (COUPON_CODES.length > 0) {
        data.coupons = COUPON_CODES
      }
      const discountsCoupon = couponsCopy.filter(coupon => {
        return coupon.isValid
      })
      captureOrderDiscounts(discountsCoupon)
    } else {
      const ALL_COUPON_CODES = orderCouponCodes
        .filter(coupon => {
          return coupon.isValid
        })
        .map(coupon => {
          return coupon.code
        })
      if (ALL_COUPON_CODES.length > 0) {
        data.coupons = ALL_COUPON_CODES
      } else {
        data.coupons = []
      }
      const discountsCoupon = orderCouponCodes.filter(coupon => {
        return coupon.isValid
      })
      captureOrderDiscounts(discountsCoupon)
    }

    try {
      // Capture order data to order_header table BEFORE sending payment link.
      // Use the returned id: order5NUId (state) isn't updated in this same tick.
      const capturedOrderId = await captureOrder5NewUpgrade(
        'send-link',
        'new',
        orderData5CU,
      )

      data.appUrl = window.location.origin
      if (capturedOrderId) {
        data.orderHeaderId = capturedOrderId
      }
      const result = await run(client('cc/order/send-link', {data}))
      if (result.message === 'Payment link sent') {
        toast.success(result.message)
        updateOrder5Status('send-link', 'Submitted', result, capturedOrderId)
      } else {
        toast.error(result.message)
        handleSendLinkFailEmail(contactMethod)
      }
      if (result.paymentUuid) {
        return result
      }
    } catch (error) {
      setProcessing(false)
      handleSendLinkFailEmail(contactMethod)
      if (error.status !== 400) {
        toast.error(`Error sending link: ${error.message}`)
        throw error
      }
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        toast.error(`Error sending link: ${error.message}`)
      }
    }
  }

  const resendPaymentLink = async current => {
    // OOE-UI-FW/OOE-UI-ER: `current.orderId` is absent whenever the order
    // header wasn't captured (or state was reset between send and resend).
    // Building `data` outside the try meant the TypeError escaped this
    // function entirely: the unhandled rejection left the caller's
    // "Resending Link..." spinner up forever with no way back. Bail out with
    // the same failure path the API-error branch already uses.
    if (!current?.orderId) {
      toast.error('Error resending link')
      handleSendLinkFailEmail(current?.method)
      return
    }
    try {
      const data = {
        orderId: current.orderId.toString(),
        country: order?.selectedCountry?.isoValue,
        method: current.method,
        email: current.email,
        appUrl: window.location.origin,
        orderHeaderId: order5NUId,
      }
      if (order?.selectedCentersAndApps) {
        data.contactPhone = current.mobile
      } else {
        data.mobile = current.mobile
      }
      // OOE-UI-AE: client() resolves undefined for 204/empty-201 responses,
      // so destructuring the result directly threw before `newUuid` could be
      // checked. `|| {}` routes those through the existing else branch.
      const {newUuid, shortenedUrl} =
        (await run(
          client(
            !order?.selectedCentersAndApps
              ? 'orders/process/resend-link'
              : 'cc/order/resend-link',
            {data, method: 'PATCH'},
          ),
        )) || {}
      if (newUuid) {
        toast.success(`${current.method} resent`)
        return {updatedUuid: newUuid, shortenedUrl}
      } else {
        toast.error('error resending link')
        handleSendLinkFailEmail(current.method)
      }
    } catch (error) {
      setProcessing(false)
      toast.error('Error resending link')
      handleSendLinkFailEmail(current.method)
    }
  }

  const checkClientEmailStatus = async current => {
    const data = {
      email: current.email,
    }

    try {
      const result = await run(
        client(`orders/email-status/${data.email}`, {method: 'GET'}),
      )

      if (result.haswarningorError) {
        toast.error(result.error_message)
      }
      if (
        result.haswarningorError === false &&
        result.show_error_msg === false
      ) {
        if (result.show_send_sms_btn) {
          setShwoSmsOption(true)
        }
      } else {
        setShwoSmsOption(false)
      }
    } catch (err) {
      toast.error(err.error)
    }
  }

  const captureOrder5NewUpgrade = async (context, orderFlow, contextData) => {
    // Returned so callers can use the freshly-created order_id synchronously —
    // setOrder5NUId() is async state and isn't readable in the same tick.
    let resolvedOrderId = order5NUId
    try {
      console.log('captureOrder5NewUpgrade() - context', context)
      console.log('captureOrder5NewUpgrade() - orderFlow', orderFlow)
      console.log('captureOrder5NewUpgrade() - contextData', contextData)
      let tempOrderData5CU = {}
      if (context === 'catalog-init-newAccOrder') {
        tempOrderData5CU = contextData
      } else {
        tempOrderData5CU = orderData5CU
      }
      const resolvedIndustryId =
        tempOrderData5CU.order?.industryCategoryId ??
        tempOrderData5CU.order?.template ??
        tempOrderData5CU.accountInfo?.industryId ??
        tempOrderData5CU.order?.accountInformation?.industryId ??
        null
      const resolvedCountryCode =
        tempOrderData5CU.order?.selectedCountry?.isoValue ??
        tempOrderData5CU.accountInfo?.billingAddress?.country ??
        tempOrderData5CU.accountInfo?.businessAddress?.country ??
        tempOrderData5CU.order?.accountInformation?.country ??
        null
      const data = {
        order_status: 'Pending',
        order_version: '5.0',
        order_flow: 'New',
        //order_submitted_date: new Date(),
        order_submitted_by: 'null',
        order_completed_date: null,
        industry_id:
          resolvedIndustryId !== null && resolvedIndustryId !== undefined
            ? resolvedIndustryId.toString()
            : null,
        industry_description:
          tempOrderData5CU.order?.industryCategoryDescription,
        country_code: resolvedCountryCode,
        account_email: tempOrderData5CU.accountInfo?.email,
        contact_first_name: tempOrderData5CU.accountInfo?.firstName,
        contact_last_name: tempOrderData5CU.accountInfo?.lastName,
        contact_phone:
          tempOrderData5CU.accountInfo?.billingAddress?.phone?.toString() ||
          tempOrderData5CU.accountInfo?.recurlyPhone?.toString() ||
          tempOrderData5CU.accountInfo?.companyPhone?.toString(),
        contact_mobile:
          tempOrderData5CU.accountInfo?.mobile?.toString() ||
          tempOrderData5CU.accountInfo?.billingAddress?.mobile?.toString() ||
          tempOrderData5CU.accountInfo?.recurlyPhone?.toString() ||
          tempOrderData5CU.accountInfo?.billingAddress?.phone?.toString(),
        contact_email: tempOrderData5CU.accountInfo?.email,
        company_name:
          tempOrderData5CU.accountInfo?.company ||
          tempOrderData5CU.accountInfo?.recurlyCompany ||
          null,
        company_address1:
          tempOrderData5CU.accountInfo?.billingAddress?.street1 ||
          tempOrderData5CU.accountInfo?.businessAddress?.street1,
        company_address2:
          tempOrderData5CU.accountInfo?.billingAddress?.street2 ||
          tempOrderData5CU.accountInfo?.businessAddress?.street2 ||
          null,
        company_city:
          tempOrderData5CU.accountInfo?.billingAddress?.city ||
          tempOrderData5CU.accountInfo?.businessAddress?.city,
        company_state:
          tempOrderData5CU.accountInfo?.billingAddress?.region ||
          tempOrderData5CU.accountInfo?.billingAddress?.state ||
          tempOrderData5CU.accountInfo?.businessAddress?.region ||
          '',
        company_zipcode:
          tempOrderData5CU.accountInfo?.billingAddress?.postalCode ||
          tempOrderData5CU.accountInfo?.billingAddress?.zipCode ||
          tempOrderData5CU.accountInfo?.businessAddress?.postalCode ||
          tempOrderData5CU.accountInfo?.postalCode ||
          '',
        // company_address1: tempOrderData5CU.accountInfo.businessAddress.street1,
        // company_address2: tempOrderData5CU.accountInfo.businessAddress.street2,
        // company_city: tempOrderData5CU.accountInfo.businessAddress.city,
        // company_state: tempOrderData5CU.accountInfo.businessAddress.region,
        // company_zipcode:
        //   tempOrderData5CU.accountInfo.businessAddress.postalCode,
        company_phone:
          tempOrderData5CU.accountInfo?.companyPhone?.toString() ||
          tempOrderData5CU.accountInfo?.billingAddress?.phone?.toString() ||
          tempOrderData5CU.accountInfo?.recurlyPhone?.toString(),
        sales_rep_id: tempOrderData5CU.accountInfo?.salesRepCode
          ? tempOrderData5CU.accountInfo?.salesRepCode
          : null, //need to check
        on_behalf_of: tempOrderData5CU.accountInfo?.behalfEmail
          ? tempOrderData5CU.accountInfo?.behalfEmail
          : null,
        xp_code: tempOrderData5CU.accountInfo?.xpCode
          ? tempOrderData5CU.accountInfo?.xpCode
          : null,
        abn: tempOrderData5CU.accountInfo?.abn
          ? tempOrderData5CU.accountInfo?.abn
          : null, //need to check,
        thryv_id: tempOrderData5CU.order?.CCAccountInfo?.thryvId,
        account_code: tempOrderData5CU.order?.CCAccountInfo?.ccId
          ? tempOrderData5CU.order?.CCAccountInfo?.ccId
          : 'null',
        eaid: tempOrderData5CU.accountInfo?.eaid
          ? tempOrderData5CU.accountInfo?.eaid
          : 'null',
        is_invoice_billed: tempOrderData5CU.accountInfo?.allowInvoice ? 1 : 0,
        is_cohort: null, //need to check,
        demo_lead_id: null,
        no_service: null, //need to check
        onboarding_appt_id: order5OnboardingApptId,
        creative_appt_id: order5CreativeApptId,
        thryv_success_services_appt_id: order5ThryvSuccessServicesApptId,
        update_to_autopay: 1, //check
        on_boarding_fee: 0, //need to check
        purchase_intent_1: null, //need to check,
        purchase_intent_2: null, //need to check,
        purchase_intent_3: null, //need to check,
        sensis_site: null, //need to check,
        lines: [],
        discounts: [],
        reseller: [],
      }

      if (context === 'catalog-init-newAccOrder') {
        // const discountsData = []
        data.order_status = 'Completed'
        data.order_completed_date = new Date()
        let lineCounter = 1
        const lines = []

        const subscriptions = [
          ...contextData.subscriptions.parentSubscriptions,
          ...contextData.subscriptions.childSubscriptions,
        ]
        subscriptions.forEach(subs => {
          const pCat = subs?.name?.includes('Center') ? 'Center' : 'App'
          let pUAmount =
            subs.unitAmount || subs.unitAmount === 0
              ? subs.unitAmount
              : subs.amount || subs.amount === 0
              ? subs.amount
              : null
          let pUPrice = pUAmount
          if (pUAmount) {
            pUPrice = pUAmount * (subs.quantity ? subs.quantity : 1)
          }
          const line = {
            line_number: lineCounter,
            parent_line_number: null,
            plansv5_id: 'vipmzc731i88',
            action_code: 'New',
            order_line_status: 'Completed',
            saas_product_category: pCat,
            saas_product_type: 'main',
            saas_product_code: subs.code,
            saas_instance_id: null,
            recurly_subscription_id: subs?.uuid ? subs?.uuid : '',
            total_cost: pUPrice,
            unit_cost: pUAmount,
            // saas_product_price: pUPrice,
            // saas_product_unit_amount: pUAmount,
            quantity: subs.quantity,
            // one_time_fee: null,
            start_date: new Date(),
            end_date: new Date(),
          }
          let p_lc = lineCounter
          lineCounter = lineCounter + 1
          lines.push(line)
          if (subs.addons && subs.addons.length > 0) {
            subs.addons.forEach(addon => {
              let cUAmount =
                addon.unitAmount || addon.unitAmount === 0
                  ? addon.unitAmount
                  : addon.amount || addon.amount === 0
                  ? addon.amount
                  : null
              let cUPrice = cUAmount
              if (cUAmount) {
                cUPrice = cUAmount * (addon.quantity ? addon.quantity : 1)
              }
              const line = {
                line_number: lineCounter,
                parent_line_number: p_lc,
                plansv5_id: null,
                action_code: 'New',
                order_line_status: 'Completed',
                saas_product_category: pCat,
                saas_product_type: pCat === 'Center' ? 'entitlement' : 'addon',
                saas_product_code: addon.code,
                saas_instance_id: null,
                recurly_subscription_id: addon.subscriptionId,
                total_cost: cUPrice,
                unit_cost: cUAmount,
                // saas_product_price: cUPrice,
                // saas_product_unit_amount: cUAmount,
                quantity: subs.quantity,
                // one_time_fee: null,
                start_date: new Date(),
                end_date: new Date(),
              }
              lineCounter = lineCounter + 1
              lines.push(line)
            })
          }
        })
        data.lines = lines
        setOrder5SelectedLines(lines)
      } else if (context === 'catalog-continue-summary') {
        data.order_status = 'Pending'
        data.lines = contextData
        data.discounts = order5LinesDiscounts
        setOrder5SelectedLines(contextData)
      } else if (context === 'capture-appointment') {
        console.log(
          'order5SelectedLines - upgrade - capture-appointment',
          order5SelectedLines,
        )
        console.log(
          'order5LinesDiscounts - upgrade - capture-appointment',
          order5LinesDiscounts,
        )
        if (contextData.appointment_type === 'creative') {
          data.creative_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'onboarding') {
          data.onboarding_appt_id = contextData.apptId
        } else if (contextData.appointment_type === 'thryv-success-services') {
          data.thryv_success_services_appt_id = contextData.apptId
        }
        data.lines = order5SelectedLines
        data.discounts = order5LinesDiscounts
      } else {
        console.log(
          'order5SelectedLines - upgrade - other context',
          order5SelectedLines,
        )
        console.log(
          'order5LinesDiscounts - upgrade - other context',
          order5LinesDiscounts,
        )
        data.lines = order5SelectedLines
        data.discounts = order5LinesDiscounts
      }
      data.keap_account = keapAccount || false

      if (isReseller) {
        const resellerData = getResellerData(data)
        console.log('resellerData', resellerData)
        if (resellerData) {
          data.reseller.push(resellerData)
        }
      }

      if (!order5NUId) {
        console.log('createOrderData - newAccorder', data)
        console.log('createOrderData - newAccorder (company fields)', {
          company_name: data.company_name,
          company_state: data.company_state,
          company_zipcode: data.company_zipcode,
        })
        const result = await run(
          client('ordercapture/services/createOrder', {data, method: 'POST'}),
        )
        console.log('order created - newAccorder', result)
        if (context === 'catalog-init-newAccOrder') {
          setOrder5NUId(null)
          setIsNewAccountOrderCreated(true)
        } else {
          setOrder5NUId(result.order_id)
          resolvedOrderId = result.order_id
        }
      } else {
        data.order_id = order5NUId
        resolvedOrderId = order5NUId
        console.log('UpdateOrderData - newAccorder', data)
        const result = await run(
          client('ordercapture/services/updateOrder', {data, method: 'POST'}),
        )
        console.log('order updated - newAccorder', result)
      }
    } catch (error) {
      if (error.status !== 400) {
        console.log(`Error creating/updating newAccorder: ${error.message}`)
      }
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        console.log(`Error creating/updating newAccorder: ${error.message}`)
      }
    }
    return resolvedOrderId
  }

  const getResellerData = data => {
    if (data.lines && data.lines.length > 0) {
      const nonCcProducts = [
        'cc_basicpls-5-thryv-m2m',
        'appmarket-5-thryv-m2m',
        'addon-5-signatures_usage',
        'app-websitebuilder',
      ]
      const isnonCCPrduct = data.lines.every(line =>
        nonCcProducts.some(prdct => prdct === line.saas_product_code),
      )
      console.log('isnonCCPrduct', isnonCCPrduct)
      if (!isnonCCPrduct) {
        let account_recurly_subscription_id
        if (data?.lines && data.lines.length > 0) {
          for (const line of data.lines) {
            // if (!nonCcProducts.includes(line.saas_product_code)) {
            if (
              line.saas_product_category === 'Center' &&
              line.recurly_subscription_id
            ) {
              account_recurly_subscription_id = line.recurly_subscription_id
              break
            }
            // }
          }
        }
        const resellerData = {
          account_code: orderData5CU.recurlyAccount.code
            ? orderData5CU.recurlyAccount.code
            : null,
          account_id: orderData5CU.recurlyAccount.billingInfo?.accountId
            ? orderData5CU.recurlyAccount.billingInfo?.accountId
            : orderData5CU.recurlyAccount.id
            ? orderData5CU.recurlyAccount.id
            : null,
          no_service: null,
          original_selling_coupon_code: null,
          subscription_id: orderData5CU.order.subscriptionId
            ? orderData5CU.order.subscriptionId
            : account_recurly_subscription_id
            ? account_recurly_subscription_id
            : null,
          thryv_id: orderData5CU.order.CCAccountInfo.thryvId,
          uuid: orderData5CU.order?.subscriptionId
            ? orderData5CU.order?.subscriptionId
            : account_recurly_subscription_id
            ? account_recurly_subscription_id
            : null,
        }
        return resellerData
      } else {
        return null
      }
      // return null
    }
  }

  const updateOrder5Status = async (
    context,
    orderFlow,
    contextData,
    orderIdOverride,
  ) => {
    console.log('updateOrder5Status() - context', context)
    console.log('updateOrder5Status() - orderFlow', orderFlow)
    console.log('updateOrder5Status() - contextData', contextData)
    try {
      const statusData = {
        order_id: orderIdOverride ?? order5NUId,
        order_status: 'Pending',
        payment_uuid: contextData?.paymentUuid,
      }
      if (context === 'send-link') {
        statusData.order_status = 'Submitted'
      } else if (context === 'cancel-order') {
        statusData.order_status = 'Cancelled'
      }
      const result = await run(
        client('ordercapture/services/updateOrderStatus', {
          data: statusData,
          method: 'PUT',
        }),
      )
      console.log('order updated - NewAcc - status', result)
    } catch (error) {
      if (error.status !== 400) {
        console.log(`Error updating order status: ${error.message}`)
        // throw error
      }
      if (error.errors) {
        error.errors.forEach(({message}) => toast.error(message))
      } else {
        console.log(`Error updating order status: ${error.message}`)
      }
    }
  }

  const updateApptOnExpire = async (appointment_type, apptId) => {
    const appointmentObj = {
      order_id: order5NUId,
      schedule_status: 'Expired',
      appointment_type: appointment_type,
      appt_id: apptId,
    }
    try {
      // const data = appointmentObj
      const result = await run(
        client('ordercapture/services/updateScheduledAppointment', {
          data: appointmentObj,
          method: 'PUT',
        }),
      )
      if (result.success) {
        if (appointment_type === 'creative') {
          setOrder5CreativeApptId(null)
        } else if (appointment_type === 'onboarding') {
          setOrder5OnboardingApptId(null)
        } else if (appointment_type === 'thryv-success-services') {
          setOrder5ThryvSuccessServicesApptId(null)
        }
        const contextData = {
          appointment_type: appointment_type,
          apptId: null,
        }
        captureOrder5NewUpgrade(
          'capture-appointment',
          'new-upgrade',
          contextData,
        )
      } else {
        console.log(result.message)
      }
    } catch (error) {
      if (error.status !== 400) {
        console.log('error in cancelling Appointment', error)
        // throw error
      }
      if (error.errors) {
        console.log('error in cancelling Appointment', error)
        // throw error
      }
    }
  }

  const captureOrderDiscounts = async discountData => {
    console.log('inside captureOrderDiscounts - ', discountData)
    const allDiscount = []
    let lineCounter = 1
    for (let data of discountData) {
      console.log('disData', data)
      // const today = new Date()
      const dataObj = {
        order_id: order5NUId,
        line_number: lineCounter,
        discount_type: data.type,
        coupon_code: data.code,
        discount_percentage: data.discountPercent,
        discount_amount: data.discountAmount,
        number_of_months: data.durationAmount,
        start_date: new Date(),
        end_date: datePlusDuration(
          new Date(),
          data.durationAmount,
          data.durationUnit,
        ),
        created_date: null,
        create_oprid: null,
        updated_date: null,
        update_oprid: null,
      }
      allDiscount.push(dataObj)
      lineCounter = lineCounter + 1
    }
    setOrder5LinesDiscounts(allDiscount)
    // captureOrder5NewUpgrade('preview-payment', 'new-upgrade', allDiscount)
  }

  function datePlusDuration(date, duration, unit) {
    const newDate = new Date(date)
    if (unit === 'month') newDate.setMonth(newDate.getMonth() + duration)
    if (unit === 'day') newDate.setDate(newDate.getDate() + duration)
    if (unit === 'year') newDate.setFullYear(newDate.getFullYear() + duration)
    return newDate
  }
  console.log({currentStep: initialStep()})
  return (
    <Container frontendSideNav={frontendSideNav}>
      <MainContainer>
        <WizardContainer>
          <StepsContainer>
            {currentStep === 0 && (
              <PartnerSelection
                nextStep={nextStep}
                prevStep={prevStep}
                partnerType={partnerType}
                setPartnerType={setPartnerType}
              />
            )}
            {currentStep === 1 && (
              <CountrySelection nextStep={nextStep} prevStep={prevStep} />
            )}

            {currentStep === 2 && (
              <IndustryCategorySelection
                nextStep={nextStep}
                prevStep={prevStep}
                selectedCountry={order.selectedCountry.isoValue}
              />
            )}
            {currentStep === 3 && (
              <IndustrySelection
                nextStep={nextStep}
                prevStep={prevStep}
                selectedCountry={order.selectedCountry}
                selectedIndustry={order.industryCategoryId}
                industryCategoryType={order.industryCategoryType}
              />
            )}
            {currentStep === 4 && (
              <AccountInformation
                nextStep={nextStep}
                prevStep={prevStep}
                selectedCountry={order.selectedCountry.isoValue}
                billingPreference={order.billingPreference}
                selectedPlan={order.selectedPlan}
                industryCategoryType={order.industryCategoryType}
                industryTemplate={order.template}
                order={order}
                currentStep={currentStep}
                showBackButton={false}
                isEaidFound={isEaidFound}
                setIsEaidFound={setIsEaidFound}
                hasKeapAccountAbility={hasKeapAccountAbility}
                partnerType={partnerType}
              />
            )}
            {currentStep === 5 && all5PlanPrices && (
              <Catalog
                nextStep={nextStep}
                prevStep={prevStep}
                selectedCountry={order.selectedCountry.isoValue}
                CCAccountInfo={order.CCAccountInfo}
                industryTemplate={order.template}
                prevCentersAndApps={order?.selectedCentersAndApps}
                prevCode={order?.code}
                prefetchedDefaultConfig={state?.defaultPlanConfig || null}
                all5PlanPrices={catalogPlanPrices}
                setIsEaidFound={setIsEaidFound}
                isEaidFound={isEaidFound}
                cohortEaid={cohortEaid}
                setCohortEaid={setCohortEaid}
                accountInfo={order.accountInformation}
                setAccountInfo={setAccountInfo}
                updateAddressInfo={updateAddressInfo}
                setAccountSubscriptionData={setAccountSubscriptionData}
                accountSubscriptionData={accountSubscriptionData}
                order={order}
                setOrder={setOrder}
                setAllowInvoice={setAllowInvoice}
                allowInvoice={allowInvoice}
                isCohort={isCohort}
                setIsCohort={setIsCohort}
                isNewAccountOrderCreated={isNewAccountOrderCreated}
                orderData5CU={orderData5CU}
                setOrderData5CU={setOrderData5CU}
                captureOrder5NewUpgrade={captureOrder5NewUpgrade}
                isWebsiteValid={isWebsiteValid}
                setIsWebsiteValid={setIsWebsiteValid}
                isNewTLMCAccessible={isNewTLMCAccessible}
                hasKeapAccountAbility={hasKeapAccountAbility}
                setHasKeapAccountAbility={setHasKeapAccountAbility}
                keapAccount={keapAccount}
                setKeapAccount={setKeapAccount}
                hasCustomDataFYAccess={hasCustomDataFYAccess}
                // hasCustomDataServiceAccess={hasCustomDataServiceAccess}
                keapID={keapID}
                setKeapID={setKeapID}
                keapTrailAppId={keapTrailAppId}
                setKeapTrailAppId={setKeapTrailAppId}
                setKeapTextVoiceQty={setKeapTextVoiceQty}
                setKeapTextVoiceQtyAnnual={setKeapTextVoiceQtyAnnual}
                setKeapAddContactsQty={setKeapAddContactsQty}
                setKeapAddContactsQtyAnnual={setKeapAddContactsQtyAnnual}
                setKeapAddContactsQtyGraphlyM2m={
                  setKeapAddContactsQtyGraphlyM2m
                }
                setPlusThisPremiumQtyGraphlyM2m={
                  setPlusThisPremiumQtyGraphlyM2m
                }
                setKeapAddContactsQtyGraphlyAnnual={
                  setKeapAddContactsQtyGraphlyAnnual
                }
                setPlusThisPremiumQtyGraphlyAnnual={
                  setPlusThisPremiumQtyGraphlyAnnual
                }
              />
            )}
            {currentStep === 6 && (
              <OnboardingProgram
                nextStep={nextStep}
                prevStep={prevStep}
                all5PlanPrices={all5PlanPrices}
                selectedPlan={order.selectedPlan}
                billingPreference={order.billingPreference}
                accountInformation={order.accountInformation}
                industryCategoryType={order.industryCategoryType}
                selectedCountry={order.selectedCountry}
                selectedAddons={order.selectedAddons}
                setCreativeApptId={setCreativeApptId}
                setKickoffApptId={setKickoffApptId}
                setThryvSuccessServicesApptId={setThryvSuccessServicesApptId}
                kickoffApptId={kickoffApptId}
                creativeApptId={creativeApptId}
                thryvSuccessServicesApptId={thryvSuccessServicesApptId}
                onboardingCallDate={onboardingCallDate}
                setOnboardingCallDate={setOnboardingCallDate}
                creativeCallDate={creativeCallDate}
                thryvSuccessServicesCallDate={thryvSuccessServicesCallDate}
                setCreativeCallDate={setCreativeCallDate}
                setThryvSuccessServicesCallDate={
                  setThryvSuccessServicesCallDate
                }
                allSelectedPurchaseIntent={allSelectedPurchaseIntent}
                setAllSelectedPurchaseIntent={setAllSelectedPurchaseIntent}
                currentStep={currentStep}
                setCartProducts={setCartProducts}
                cartProducts={cartProducts}
                selectedIndustryId={order.industryCategoryId}
                selectedCentersAndApps={effectiveSelectedCentersAndApps}
                country={order.selectedCountry.isoValue}
                upgradeAccountInfo={accountInfo}
                isCohort={isCohort}
                order5NUId={order5NUId}
                isCancelOrder5={true}
                cancelOrder5={updateOrder5Status}
                // setOrder5AllDiscounts={setOrder5AllDiscounts}
                captureOrderNewUpgrade={captureOrder5NewUpgrade}
                setOrder5CreativeApptId={setOrder5CreativeApptId}
                setOrder5ThryvSuccessServicesApptId={
                  setOrder5ThryvSuccessServicesApptId
                }
                setOrder5OnboardingApptId={setOrder5OnboardingApptId}
                isWebsiteValid={isWebsiteValid}
                isNewTLMCAccessible={isNewTLMCAccessible}
                keapAccount={keapAccount}
              />
            )}
            {currentStep === 7 && (
              <PaymentDetails
                resendPaymentLink={resendPaymentLink}
                checkClientEmailStatus={checkClientEmailStatus}
                showSmsOption={showSmsOption}
                setShwoSmsOption={setShwoSmsOption}
                nextStep={nextStep}
                prevStep={prevStep}
                selectedAddons={order.selectedAddons}
                selectedCountry={order.selectedCountry.value}
                accountInformation={order.accountInformation}
                planCode={order.package}
                countryIsoValue={order.selectedCountry.isoValue}
                creativeApptId={creativeApptId}
                thryvSuccessServicesApptId={thryvSuccessServicesApptId}
                kickoffApptId={kickoffApptId}
                setKickoffApptId={setKickoffApptId}
                setCreativeApptId={setCreativeApptId}
                setThryvSuccessServicesApptId={setThryvSuccessServicesApptId}
                setCartProducts={setCartProducts}
                accountInfo={accountInfo}
                cartProducts={cartProducts}
                currentStep={currentStep}
                processing={processing}
                setProcessing={setProcessing}
                setCreativeCallDate={setCreativeCallDate}
                setThryvSuccessServicesCallDate={
                  setThryvSuccessServicesCallDate
                }
                setOnboardingCallDate={setOnboardingCallDate}
                selectedPlan={order.selectedPlan}
                clientEmail={order.accountInformation.email}
                selectedCentersAndApps={effectiveSelectedCentersAndApps}
                sendCCPaymentLink={sendCCPaymentLink}
                onboardingCallDate={onboardingCallDate}
                creativeCallDate={creativeCallDate}
                thryvSuccessServicesCallDate={thryvSuccessServicesCallDate}
                isSEOIncluded={isSEOIncluded}
                allowInvoice={allowInvoice}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                selectedPaymentMethod={selectedPaymentMethod}
                isCohort={isCohort}
                cohortEaid={cohortEaid}
                orderData5CU={orderData5CU}
                order5NUId={order5NUId}
                isCancelOrder5={true}
                cancelOrder5={updateOrder5Status}
                updateApptOnExpire={updateApptOnExpire}
                isWebsiteValid={isWebsiteValid}
                isNewTLMCAccessible={isNewTLMCAccessible}
                keapAccount={keapAccount}
                all5PlanPrices={all5PlanPrices}
                order={order}
                keapTrailAppId={keapTrailAppId}
                setKeapTrailAppId={setKeapTrailAppId}
                keapTextVoiceQty={keapTextVoiceQty}
                keapAddContactsQty={keapAddContactsQty}
                keapAddContactsQtyAnnual={keapAddContactsQtyAnnual}
                keapTextVoiceQtyAnnual={keapTextVoiceQtyAnnual}
              />
            )}
            {currentStep === 8 && (
              <Commitment
                prevStep={prevStep}
                clientInitials={clientInitials}
                updateInitials={updateInitials}
                purchaseIntents={allSelectedPurchaseIntent}
                onboardingCallDate={onboardingCallDate}
                creativeCallDate={creativeCallDate}
                thryvSuccessServicesCallDate={thryvSuccessServicesCallDate}
                clientEmail={order.accountInformation.email}
              />
            )}
          </StepsContainer>
        </WizardContainer>
      </MainContainer>
    </Container>
  )
}

export {NewOrder}
